import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShoppingCart, Package, FileText, Plus, X, Trash, Check, Trash2, LogOut, Key, Edit } from 'lucide-react';
import { Product, CartItem, Order } from './types';
import { api } from './api';
import ProductForm from './components/ProductForm';
import ImageWithFallback from './components/ImageWithFallback';
import CurrencySelector from './components/CurrencySelector';
import { formatCurrency, loadCurrencyFromStorage, getCurrentCurrency } from './config';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import PasswordChange from './components/PasswordChange';


function App() {
  const { isAuthenticated, isLoading, login, logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'pos' | 'cart' | 'orders' | 'products'>('pos');
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loginError, setLoginError] = useState<string>('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'twint'>('cash');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'all' | 'cash' | 'card' | 'twint'>('all');
  const [selectedDateFilter, setSelectedDateFilter] = useState<'all' | 'today' | 'month-to-date' | 'past-month'>('all');
  const [showDateFilterMenu, setShowDateFilterMenu] = useState(false);
  const [showPaymentMethodMenu, setShowPaymentMethodMenu] = useState(false);

  // Add debugging for authentication state
  console.log('App render - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'user:', user);

  // Load data when authenticated
  useEffect(() => {
    console.log('App useEffect - isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      loadCurrencyFromStorage();
      loadProducts();
      loadOrders();
      loadCartFromStorage();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    saveCartToStorage();
  }, [cart]);

  const loadProducts = useCallback(async () => {
    try {
      const data = await api.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      setMessage({ type: 'error', text: 'Failed to load products' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const data = await api.getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  }, []);

  const loadCartFromStorage = useCallback(() => {
    try {
      const savedCart = localStorage.getItem('simplepos-cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from storage:', error);
    }
  }, []);

  const saveCartToStorage = useCallback(() => {
    try {
      localStorage.setItem('simplepos-cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to storage:', error);
    }
  }, [cart]);

  const addToCart = useCallback((product: Product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.price }
            : item
        );
      } else {
        return [...prevCart, {
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity: 1,
          subtotal: product.price
        }];
      }
    });
  }, []);

  const handleLogin = async (username: string, password: string) => {
    setLoginError('');
    try {
      const success = await login(username, password);
      if (success) {
        setLoginError('');
      } else {
        setLoginError('Invalid username or password');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    }
  };

  // Add click outside handler for user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showUserMenu && !target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Add click outside handler for filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showDateFilterMenu && !target.closest('.filter-dropdown')) {
        setShowDateFilterMenu(false);
      }
      if (showPaymentMethodMenu && !target.closest('.filter-dropdown')) {
        setShowPaymentMethodMenu(false);
      }
    };

    if (showDateFilterMenu || showPaymentMethodMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateFilterMenu, showPaymentMethodMenu]);

  // Move authentication check to after all hooks are defined

  const updateQuantity = useCallback((productId: number, change: number) => {
    setCart(prevCart => {
      const newCart = prevCart.map(item => {
        if (item.product_id === productId) {
          const newQuantity = Math.max(0, item.quantity + change);
          return {
            ...item,
            quantity: newQuantity,
            subtotal: newQuantity * item.price
          };
        }
        return item;
      }).filter(item => item.quantity > 0);
      
      return newCart;
    });
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  }, []);

  const getTotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.subtotal, 0);
  }, [cart]);

  const handleCheckout = useCallback(async () => {
    if (cart.length === 0) return;

    try {
      const orderData = {
        items: cart,
        total_amount: getTotal(),
        payment_method: paymentMethod
      };

      await api.createOrder(orderData);
      
      setMessage({ type: 'success', text: `Order completed successfully! Paid with ${paymentMethod.toUpperCase()}` });
      setCart([]);
      setPaymentMethod('cash'); // Reset to default payment method
      loadOrders();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error creating order:', error);
      setMessage({ type: 'error', text: 'Failed to create order' });
    }
  }, [cart, getTotal, paymentMethod, loadOrders]);

  const handleProductSave = useCallback(async (productData: Partial<Product>) => {
    try {
      if (editingProduct) {
        await api.updateProduct(editingProduct.id, productData);
        setMessage({ type: 'success', text: 'Product updated successfully!' });
      } else {
        await api.createProduct(productData);
        setMessage({ type: 'success', text: 'Product created successfully!' });
      }
      
      loadProducts();
      setShowProductForm(false);
      setEditingProduct(null);
      
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving product:', error);
      setMessage({ type: 'error', text: 'Failed to save product' });
    }
  }, [editingProduct, loadProducts]);

  const handleProductDelete = useCallback(async (productId: number) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await api.deleteProduct(productId);
      setMessage({ type: 'success', text: 'Product deleted successfully!' });
      loadProducts();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage({ type: 'error', text: 'Failed to delete product' });
    }
  }, [loadProducts]);

  const editProduct = useCallback((product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  }, []);

  const clearMessage = useCallback(() => setMessage(null), []);

  const handleOrderStatusUpdate = useCallback(async (orderId: number, status: string) => {
    try {
      await api.updateOrder(orderId, { status });
      setMessage({ type: 'success', text: `Order marked as ${status}!` });
      loadOrders();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error updating order:', error);
      setMessage({ type: 'error', text: 'Failed to update order' });
    }
  }, [loadOrders]);

  const handleOrderDelete = useCallback(async (orderId: number) => {
    if (!confirm('Are you sure you want to delete this order?')) return;

    try {
      await api.deleteOrder(orderId);
      setMessage({ type: 'success', text: 'Order deleted successfully!' });
      loadOrders();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting order:', error);
      setMessage({ type: 'error', text: 'Failed to delete order' });
    }
  }, [loadOrders]);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category))];
    return ['all', ...uniqueCategories.sort()];
  }, [products]);

  // Filter products by category and search
  const filteredProducts = useMemo(() => {
    let filtered = products;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // Filter orders by payment method and date
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    // Filter by payment method
    if (selectedPaymentMethod !== 'all') {
      filtered = filtered.filter(order => order.payment_method === selectedPaymentMethod);
    }
    
    // Filter by date
    if (selectedDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        
        switch (selectedDateFilter) {
          case 'today':
            return orderDate >= today;
          case 'month-to-date':
            return orderDate >= startOfThisMonth;
          case 'past-month':
            return orderDate >= startOfLastMonth && orderDate <= endOfLastMonth;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [orders, selectedPaymentMethod, selectedDateFilter]);

  // Calculate total for filtered orders
  const filteredOrdersTotal = useMemo(() => {
    return filteredOrders.reduce((sum, order) => sum + order.total_amount, 0);
  }, [filteredOrders]);

  // Only show loading spinner if we're still loading data AND user is authenticated
  if (loading && isAuthenticated) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading...
      </div>
    );
  }

  // Show loading spinner while checking authentication state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} error={loginError} />;
  }

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <h1>SimplePOS</h1>
          </div>
          <div className="header-actions">
            <div className="user-menu">
              <button
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#374151'
                }}
              >
                <span>{user}</span>
                <span style={{ fontSize: '0.75rem' }}>▼</span>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown" style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.25rem',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  zIndex: 1000,
                  minWidth: '200px'
                }}>
                  <div className="user-menu-header" style={{
                    padding: '0.75rem',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div style={{ fontWeight: '600', color: '#374151' }}>Welcome, {user}!</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Manage your account</div>
                  </div>
                  
                  <div style={{
                    padding: '0.75rem',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginBottom: '0.5rem',
                      fontWeight: '500'
                    }}>
                      Currency
                    </div>
                    <CurrencySelector onCurrencyChange={() => {
                      // Force re-render to update all price displays
                      setProducts([...products]);
                      setOrders([...orders]);
                    }} />
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowPasswordChange(true);
                      setShowUserMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      textAlign: 'left',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#374151',
                      fontSize: '0.875rem',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Key size={16} />
                    Change Password
                  </button>
                  
                  <button
                    onClick={logout}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      textAlign: 'left',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#dc2626',
                      fontSize: '0.875rem',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="container">
          {message && (
            <div className={`${message.type === 'success' ? 'success-message' : 'error-message'}`}>
              {message.text}
              <button onClick={clearMessage} style={{ background: 'none', border: 'none', color: 'white', marginLeft: '1rem', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
          )}

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'pos' ? 'active' : ''}`}
              onClick={() => setActiveTab('pos')}
            >
              <ShoppingCart size={16} style={{ marginRight: '0.5rem' }} />
              Point of Sale
            </button>
            <button
              className={`tab ${activeTab === 'cart' ? 'active' : ''}`}
              onClick={() => setActiveTab('cart')}
            >
              <ShoppingCart size={16} style={{ marginRight: '0.5rem' }} />
              Shopping Cart
              {cart.reduce((total, item) => total + item.quantity, 0) > 0 && (
                <span style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '0.125rem 0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  marginLeft: '0.5rem',
                  minWidth: '1.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {cart.reduce((total, item) => total + item.quantity, 0)}
                </span>
              )}
            </button>
            <button
              className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <FileText size={16} style={{ marginRight: '0.5rem' }} />
              Orders
            </button>
            <button
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              <Package size={16} style={{ marginRight: '0.5rem' }} />
              Products
            </button>

          </div>

          {activeTab === 'pos' && (
            <div className="products-section">
              <div className="products-header">
                <h2>Products</h2>
                <span>{filteredProducts.length} products available</span>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginTop: '0.25rem'
                }}>
                  💡 Tip: Double-click/tap products or use the 🛒+ button
                </div>
              </div>
              
              {/* Search and Filter Section */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* Search Input */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        color: '#6b7280'
                      }}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Category Filter */}
                <div className="category-filter">
                  <span className="category-filter-label">
                    Filter by:
                  </span>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`category-filter-button ${selectedCategory === category ? 'active' : ''}`}
                      style={{
                        textTransform: category === 'all' ? 'none' : 'capitalize'
                      }}
                    >
                      {category === 'all' ? 'All Categories' : category}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="products-list">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="product-row">
                    <div className="product-row-image">
                      <ImageWithFallback
                        src={product.image_url}
                        alt={product.name}
                        className="product-thumbnail"
                      />
                    </div>
                    <div className="product-row-info">
                      <div className="product-row-name">{product.name}</div>
                      <div className="product-row-details">
                        <span className="product-row-price">{formatCurrency(product.price)}</span>
                        <span className="product-row-category">{product.category}</span>
                      </div>
                    </div>
                    <div className="product-row-action">
                      <button
                        onClick={() => addToCart(product)}
                        className="add-button"
                        title="Add to cart"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <ShoppingCart size={16} />
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'cart' && (
            <div className="cart-section" style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div className="cart-header">
                <h2>Shopping Cart</h2>
                <span>{cart.reduce((total, item) => total + item.quantity, 0)} items</span>
              </div>
              
              {cart.length === 0 ? (
                <div className="empty-cart">
                  <div className="empty-cart-icon">🛒</div>
                  <p>Your cart is empty</p>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Go to Point of Sale to add products
                  </p>
                </div>
              ) : (
                <>
                  <div className="cart-items" style={{ marginBottom: '1rem' }}>
                    {cart.map((item) => (
                      <div key={item.product_id} className="cart-item">
                        <ImageWithFallback
                          src={products.find(p => p.id === item.product_id)?.image_url || ''}
                          alt={item.name}
                          className="cart-item-image"
                        />
                        <div className="cart-item-details">
                          <div className="cart-item-name">{item.name}</div>
                          <div className="cart-item-price">{formatCurrency(item.price)} each</div>
                        </div>
                        <div className="quantity-controls">
                          <button
                            className="quantity-btn"
                            onClick={() => updateQuantity(item.product_id, -1)}
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            className="quantity-btn"
                            onClick={() => updateQuantity(item.product_id, 1)}
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.product_id)}
                            className="remove-btn"
                            title="Remove item"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="cart-total">
                    <div className="total-row">
                      <span className="total-label">Total:</span>
                      <span className="total-amount">{formatCurrency(getTotal())}</span>
                    </div>
                    
                    {/* Payment Method Selector */}
                    <div style={{
                      marginTop: '1rem',
                      marginBottom: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.75rem'
                      }}>
                        Payment Method
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setPaymentMethod('cash')}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: `2px solid ${paymentMethod === 'cash' ? '#2563eb' : '#d1d5db'}`,
                            borderRadius: '0.375rem',
                            backgroundColor: paymentMethod === 'cash' ? '#eff6ff' : 'white',
                            color: paymentMethod === 'cash' ? '#2563eb' : '#6b7280',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '80px'
                          }}
                        >
                          💵 Cash
                        </button>
                        <button
                          onClick={() => setPaymentMethod('card')}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: `2px solid ${paymentMethod === 'card' ? '#2563eb' : '#d1d5db'}`,
                            borderRadius: '0.375rem',
                            backgroundColor: paymentMethod === 'card' ? '#eff6ff' : 'white',
                            color: paymentMethod === 'card' ? '#2563eb' : '#6b7280',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '80px'
                          }}
                        >
                          💳 Card
                        </button>
                        <button
                          onClick={() => setPaymentMethod('twint')}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: `2px solid ${paymentMethod === 'twint' ? '#2563eb' : '#d1d5db'}`,
                            borderRadius: '0.375rem',
                            backgroundColor: paymentMethod === 'twint' ? '#eff6ff' : 'white',
                            color: paymentMethod === 'twint' ? '#2563eb' : '#6b7280',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '80px'
                          }}
                        >
                          📱 Twint
                        </button>
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '0.5rem',
                        textAlign: 'center'
                      }}>
                        Selected: <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{paymentMethod}</span>
                      </div>
                    </div>
                    
                    <button
                      className="checkout-btn"
                      onClick={handleCheckout}
                      disabled={cart.length === 0}
                    >
                      Complete Order ({paymentMethod.toUpperCase()})
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="orders-section">
              <div className="orders-header">
                <h2>Order History</h2>
                <span>{orders.length} orders</span>
              </div>
              
              {/* Filter Navigation Bar */}
              <div style={{
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}>
                  {/* Date Filter Dropdown */}
                  <div className="filter-dropdown" style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowDateFilterMenu(!showDateFilterMenu)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        minWidth: '160px',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        📅 {selectedDateFilter === 'all' ? 'All Time' : 
                            selectedDateFilter === 'today' ? 'Today' :
                            selectedDateFilter === 'month-to-date' ? 'Month to Date' :
                            selectedDateFilter === 'past-month' ? 'Past Month' : 'All Time'}
                      </span>
                      <span style={{ fontSize: '0.75rem' }}>▼</span>
                    </button>
                    
                    {showDateFilterMenu && (
                      <div className="filter-dropdown-menu" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '160px'
                      }}>
                        {(['all', 'today', 'month-to-date', 'past-month'] as const).map((dateFilter) => (
                          <button
                            key={dateFilter}
                            onClick={() => {
                              setSelectedDateFilter(dateFilter);
                              setShowDateFilterMenu(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: selectedDateFilter === dateFilter ? '#059669' : '#374151',
                              fontSize: '0.875rem',
                              transition: 'background-color 0.2s ease',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {dateFilter === 'all' && '📅'}
                            {dateFilter === 'today' && '🗓️'}
                            {dateFilter === 'month-to-date' && '📊'}
                            {dateFilter === 'past-month' && '📆'}
                            {dateFilter === 'all' ? 'All Time' : 
                             dateFilter === 'today' ? 'Today' :
                             dateFilter === 'month-to-date' ? 'Month to Date' :
                             dateFilter === 'past-month' ? 'Past Month' : 'All Time'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Payment Method Filter Dropdown */}
                  <div className="filter-dropdown" style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowPaymentMethodMenu(!showPaymentMethodMenu)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        minWidth: '160px',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {selectedPaymentMethod === 'all' ? '📊' :
                         selectedPaymentMethod === 'cash' ? '💵' :
                         selectedPaymentMethod === 'card' ? '💳' :
                         selectedPaymentMethod === 'twint' ? '📱' : '📊'}
                        {selectedPaymentMethod === 'all' ? 'All Methods' : selectedPaymentMethod.toUpperCase()}
                      </span>
                      <span style={{ fontSize: '0.75rem' }}>▼</span>
                    </button>
                    
                    {showPaymentMethodMenu && (
                      <div className="filter-dropdown-menu" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '160px'
                      }}>
                        {(['all', 'cash', 'card', 'twint'] as const).map((method) => (
                          <button
                            key={method}
                            onClick={() => {
                              setSelectedPaymentMethod(method);
                              setShowPaymentMethodMenu(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: selectedPaymentMethod === method ? '#2563eb' : '#374151',
                              fontSize: '0.875rem',
                              transition: 'background-color 0.2s ease',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {method === 'all' && '📊'}
                            {method === 'cash' && '💵'}
                            {method === 'card' && '💳'}
                            {method === 'twint' && '📱'}
                            {method === 'all' ? 'All Methods' : method.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Active Filters Display */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#6b7280'
                  }}>
                    <span>Active filters:</span>
                    {selectedDateFilter !== 'all' && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#ecfdf5',
                        color: '#059669',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {selectedDateFilter === 'today' ? 'Today' :
                         selectedDateFilter === 'month-to-date' ? 'Month to Date' :
                         selectedDateFilter === 'past-month' ? 'Past Month' : ''}
                      </span>
                    )}
                    {selectedPaymentMethod !== 'all' && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#eff6ff',
                        color: '#2563eb',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {selectedPaymentMethod.toUpperCase()}
                      </span>
                    )}
                    {(selectedDateFilter === 'all' && selectedPaymentMethod === 'all') && (
                      <span style={{ fontStyle: 'italic' }}>None</span>
                    )}
                  </div>
                </div>

                {/* Total Summary */}
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Total for {selectedPaymentMethod === 'all' ? 'All Methods' : selectedPaymentMethod.toUpperCase()}
                      {selectedDateFilter !== 'all' && ` - ${
                        selectedDateFilter === 'today' ? 'Today' :
                        selectedDateFilter === 'month-to-date' ? 'Month to Date' :
                        selectedDateFilter === 'past-month' ? 'Past Month' : ''
                      }`}:
                    </span>
                    <span style={{
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      color: '#059669'
                    }}>
                      {formatCurrency(filteredOrdersTotal)}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    marginTop: '0.25rem'
                  }}>
                    {filteredOrders.length} orders
                  </div>
                </div>
              </div>
              
              {filteredOrders.length === 0 ? (
                <div className="empty-cart">
                  <div className="empty-cart-icon">📋</div>
                  <p>No orders found</p>
                  {(selectedPaymentMethod !== 'all' || selectedDateFilter !== 'all') && (
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      No orders found for: {selectedPaymentMethod !== 'all' ? selectedPaymentMethod.toUpperCase() : 'All Methods'}
                      {selectedDateFilter !== 'all' && ` - ${
                        selectedDateFilter === 'today' ? 'Today' :
                        selectedDateFilter === 'month-to-date' ? 'Month to Date' :
                        selectedDateFilter === 'past-month' ? 'Past Month' : ''
                      }`}
                    </p>
                  )}
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div key={order.id} className="order-item">
                    <div className="order-header">
                      <div className="order-info">
                        <span className="order-id">{order.order_id}</span>
                        <span className={`order-status ${order.status}`}>{order.status}</span>
                      </div>
                      <div className="order-actions">
                        {order.status !== 'delivered' && (
                          <button
                            onClick={() => handleOrderStatusUpdate(order.id, 'delivered')}
                            className="order-action-btn delivered-btn"
                            title="Mark as delivered"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleOrderDelete(order.id)}
                          className="order-action-btn delete-btn"
                          title="Delete order"
                        >
                          <Trash2 size={16} />
                        </button>
                        <span className="order-total">{formatCurrency(order.total_amount)}</span>
                      </div>
                    </div>
                    <div className="order-date">
                      {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                      <div className="order-items">
                        {order.items.map((item, index) => (
                          <span key={index}>
                            {item.quantity}x {item.name}
                            {index < order.items.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '0.25rem',
                        color: '#6b7280',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                      }}>
                        {order.payment_method || 'cash'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'products' && (
            <div className="products-section">
              <div className="products-header">
                <h2>Product Management</h2>
                <button
                  className="btn"
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductForm(true);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.75rem',
                    minWidth: '44px',
                    height: '44px'
                  }}
                  title="Add Product"
                >
                  <Plus size={20} />
                </button>
              </div>
              
              <div className="products-list">
                {products.map((product) => (
                  <div key={product.id} className="product-row">
                    <div className="product-row-image">
                      <ImageWithFallback
                        src={product.image_url}
                        alt={product.name}
                        className="product-thumbnail"
                      />
                    </div>
                    <div className="product-row-info">
                      <div className="product-row-name">{product.name}</div>
                      <div className="product-row-details">
                        <span className="product-row-price">{formatCurrency(product.price)}</span>
                        <span className="product-row-category">{product.category}</span>
                      </div>
                    </div>
                    <div className="product-row-actions">
                      <button
                        onClick={() => editProduct(product)}
                        className="action-button edit-button"
                        title="Edit product"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleProductDelete(product.id)}
                        className="action-button delete-button"
                        title="Delete product"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


        </div>
      </main>

      {showProductForm && (
        <ProductForm
          product={editingProduct}
          onSave={handleProductSave}
          onCancel={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
        />
      )}

      {showPasswordChange && (
        <PasswordChange
          onClose={() => setShowPasswordChange(false)}
        />
      )}
    </div>
  );
}

export default App;
